import { Locale } from "@forestsync/ui";

// Supported locale configuration - Only English active; other languages pending manual review
const localeConfig = [
  { code: "en", name: "English", countryCode: "GB" },
];

// Available locales - flags will be rendered by the LanguageSelector component
export const locales: Locale[] = localeConfig.map((locale) => ({
  value: locale.code,
  label: locale.name,
  countryCode: locale.countryCode,
}));

// Default locale
export const defaultLocale = "en";

// Helper function to get locale by code
export const getLocaleByCode = (code: string): Locale | undefined => {
  return locales.find((locale) => locale.value === code);
};

// Get all locale codes
export const getLocaleCodes = (): string[] => {
  return localeConfig.map((locale) => locale.code);
};

export async function getMessages(locale: string, app?: "admin" | "registry" | "marketplace") {
  try {
    // Load common translations (shared across all apps)
    const common = (await import(`../locales/${locale}/common.json`)).default;

    // Load app-specific translations if app is specified
    let appMessages: Record<string, any> = {};
    if (app) {
      try {
        appMessages = (await import(`../locales/${locale}/${app}.json`)).default;
      } catch (error) {
        // Fallback to English for app-specific translations
        console.warn(`Missing ${app} translations for ${locale}, using English`);
        appMessages = (await import(`../locales/en/${app}.json`)).default;
      }
    }

    // Merge common and app-specific translations
    // We explicitly deep merge known shared namespaces to allow apps to extend or override
    // rather than completely replacing the common namespace
    return {
      ...common,
      ...appMessages,
      common: { ...common.common, ...(appMessages.common || {}) },
      error: { ...common.error, ...(appMessages.error || {}) },
      navigation: { ...common.navigation, ...(appMessages.navigation || {}) },
    };
  } catch (error) {
    console.error(`Failed to load messages for locale ${locale}`, error);
    // Fallback to English common + app
    const common = (await import(`../locales/${defaultLocale}/common.json`)).default;
    const appMessages: Record<string, any> = app
      ? (await import(`../locales/${defaultLocale}/${app}.json`)).default
      : {};

    return {
      ...common,
      ...appMessages,
      common: { ...common.common, ...(appMessages.common || {}) },
      error: { ...common.error, ...(appMessages.error || {}) },
      navigation: { ...common.navigation, ...(appMessages.navigation || {}) },
    };
  }
}
